import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Crown, Trash2, Share2, ChevronDown, ChevronUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function MyBakes() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [bakes, setBakes] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    api.get("/crumb/mine").then((r) => setBakes(r.data));
  }, [user, loading, nav]);

  const del = async (id) => {
    if (!window.confirm("Delete this analysis? This can't be undone.")) return;
    setBusy(id);
    try {
      await api.delete(`/crumb/${id}`);
      setBakes((prev) => prev.filter((b) => b.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };

  const postForum = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/crumb/${id}/post`);
      toast.success("Posted to forum!");
      nav(`/p/${data.post_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };

  if (loading || !user) return null;

  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              {user.is_premium ? <Crown className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} /> : <Sparkles className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} />}
              <span>{user.is_premium ? "Premium member" : "Your bake log"}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              My <span style={{ color: "#F4A83B" }}>Bakes</span>
            </h1>
            <p className="mt-2 text-[#857163]">Every analysis you've run, saved for future reference and side-by-side comparison.</p>
          </div>
          <Link
            to="/analyzer"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white font-semibold text-sm hover:opacity-95 transition shrink-0"
            style={{ background: "linear-gradient(135deg, #F4A83B 0%, #E8842B 100%)" }}
            data-testid="new-analysis-btn"
          ><Sparkles className="w-4 h-4" /> New analysis</Link>
        </div>

        {bakes.length === 0 ? (
          <div className="cp-card p-10 text-center">
            <div className="font-heading text-2xl text-[#3B2C24] mb-2">No bakes yet</div>
            <p className="text-[#857163] mb-4">Analyze your first bake to start building your bake log.</p>
            <Link
              to="/analyzer"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white font-semibold text-sm"
              style={{ background: "linear-gradient(135deg, #F4A83B 0%, #E8842B 100%)" }}
            ><Sparkles className="w-4 h-4" /> Start analyzing</Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {bakes.map((b) => {
              const a = b.analysis || {};
              const isOpen = !!expanded[b.id];
              return (
                <div key={b.id} className="cp-card overflow-hidden" data-testid={`bake-${b.id}`}>
                  <div className="grid sm:grid-cols-[160px_1fr] gap-0">
                    <img src={fileUrl(b.image_path)} alt="" className="w-full h-40 sm:h-full object-cover" />
                    <div className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="cp-tag" style={{ background: "rgba(244,168,59,0.15)", color: "#8A5A0B" }}>{b.bake_type}</span>
                            <span className="text-xs text-[#857163]">
                              {(() => { try { return formatDistanceToNow(new Date(b.created_at), { addSuffix: true }); } catch { return ""; } })()}
                            </span>
                            {b.posted_to_forum && (
                              <span className="cp-tag" style={{ background: "rgba(76,159,112,0.15)", color: "#2F6A47" }}>Posted</span>
                            )}
                          </div>
                          <h3 className="font-heading text-lg text-[#3B2C24] mt-1 leading-snug">{a.diagnosis || "Analysis"}</h3>
                          <div className="text-xs text-[#857163] mt-1">Confidence: <b style={{ color: "#F4A83B" }}>{a.confidence ?? "—"}%</b>{typeof a.quality_score === "number" && <> · Quality: <b>{a.quality_score}/100</b></>}</div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            onClick={() => setExpanded((p) => ({ ...p, [b.id]: !isOpen }))}
                            className="px-3 py-1.5 rounded-full text-xs border border-[#EBDCD1] hover:bg-[#F9EFE6] inline-flex items-center gap-1"
                            data-testid={`toggle-${b.id}`}
                          >
                            {isOpen ? <>Hide <ChevronUp className="w-3 h-3" /></> : <>Details <ChevronDown className="w-3 h-3" /></>}
                          </button>
                          {!b.posted_to_forum && (
                            <button
                              onClick={() => postForum(b.id)}
                              disabled={busy === b.id}
                              className="px-3 py-1.5 rounded-full text-xs border border-[#EBDCD1] hover:bg-[#F9EFE6] inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`post-${b.id}`}
                            ><Share2 className="w-3 h-3" /> Post</button>
                          )}
                          <button
                            onClick={() => del(b.id)}
                            disabled={busy === b.id}
                            className="px-2 py-1.5 rounded-full text-xs border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                            data-testid={`delete-${b.id}`}
                          ><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="mt-3 pt-3 border-t border-[#EBDCD1] text-sm space-y-3">
                          {(b.hydration || b.proofing || b.flour || b.notes) && (
                            <div className="text-xs text-[#857163] space-x-3">
                              {b.hydration && <span>Hydration: <b className="text-[#3B2C24]">{b.hydration}</b></span>}
                              {b.proofing && <span>Proofing: <b className="text-[#3B2C24]">{b.proofing}</b></span>}
                              {b.flour && <span>Flour: <b className="text-[#3B2C24]">{b.flour}</b></span>}
                              {b.notes && <span>Notes: <b className="text-[#3B2C24]">{b.notes}</b></span>}
                            </div>
                          )}
                          {a.positives?.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-[#3B2C24] mb-1">What's working</div>
                              <ul className="text-sm text-[#3B2C24] list-disc pl-5">{a.positives.map((p, i) => <li key={i}>{p}</li>)}</ul>
                            </div>
                          )}
                          {a.causes?.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-[#3B2C24] mb-1">Possible causes</div>
                              <ul className="text-sm text-[#3B2C24] list-disc pl-5">{a.causes.map((c, i) => <li key={i}>{c}</li>)}</ul>
                            </div>
                          )}
                          {a.recommendations?.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-[#3B2C24] mb-1">Recommendations</div>
                              <ul className="text-sm text-[#3B2C24] list-disc pl-5">{a.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </>
  );
}
